import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { RealtimeService, RealtimeEvent, UserEvent } from '../../services/realtime.service';
import { DraftService } from '../../services/draft.service';
import { ComposeService } from '../../services/compose.service';
import { IAccount } from '../../models/account.models';
import { InboxFolderType } from '../../models/inbox.models';
import { AppState } from 'src/app/core/app.state';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { FetchAllUsers, FetchTeamOptions } from 'src/app/features/settings/store/settings.actions';
import { toggle } from 'src/app/shared/store/shared.actions';
import { InboxSidebarComponent } from './inbox-sidebar/inbox-sidebar.component';
import { ConversationListComponent } from './conversation-list/conversation-list.component';
import { ConversationDetailComponent } from './conversation-detail/conversation-detail.component';
import { ComposeMinimizedBarComponent } from './compose/compose-minimized-bar/compose-minimized-bar.component';
import * as smartInboxActions from '../../store/smart-inbox.actions';
import * as smartInboxSelectors from '../../store/smart-inbox.selectors';
import { IInboxFilter } from '../../store/smart-inbox.reducers';

/**
 * Top-level layout orchestrator for the smart inbox feature.
 * Composes the sidebar, conversation list, conversation detail, and compose
 * minimized bar. Owns the SSE real-time connection lifecycle.
 */
@Component({
  selector: 'app-inbox-view',
  standalone: true,
  templateUrl: './inbox-view.component.html',
  styleUrls: ['./inbox-view.component.scss'],
  imports: [
    CommonModule,
    InboxSidebarComponent,
    ConversationListComponent,
    ConversationDetailComponent,
    ComposeMinimizedBarComponent,
  ],
  providers: [RealtimeService],
})
export class InboxComponent implements OnInit, OnDestroy {

  // ── Private properties ─────────────────────────────────────────────────────

  private accounts: IAccount[] = [];
  private filter: IInboxFilter = {};
  private searchTerm = '';
  private destroy$ = new Subject<void>();

  /** Mailbox IDs with a live SSE stream, for the reconcile in `syncMailboxEventStreams`. */
  private connectedMailboxIds = new Set<number>();

  /** `accounts` ID set behind the last `LoadFoldersKPIs`. */
  private lastKpiAccountIds: string | null = null;

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private realtimeService: RealtimeService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private draftService: DraftService,
    private composeService: ComposeService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.store.select(smartInboxSelectors.selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.accounts = accounts;
        this.syncMailboxEventStreams();

        // Gated on the ID set: the selector fires on every sync transition of every
        // mailbox, and `getFoldersKPIs` reads only the ids.
        const kpiAccountIds = this.accountIdsKey(accounts);
        if (accounts.length > 0 && kpiAccountIds !== this.lastKpiAccountIds) {
          this.lastKpiAccountIds = kpiAccountIds;
          this.store.dispatch(smartInboxActions.LoadFoldersKPIs({ accounts }));
        }
      });

    this.store.select(smartInboxSelectors.selectFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filter) => {
        this.filter = filter;
        this.searchTerm = filter.searchTerm ?? '';

        // Unguarded: the reconcile no-ops when nothing moved, and the old
        // `filter.activeAccounts` guard missed clearing the filter (falls back to all).
        this.syncMailboxEventStreams();
      });

    this.applyQueryParams();

    // Collapse sidebar by default
    this.store.dispatch(toggle({ toggle: true }));
    this.store.dispatch(FetchAllUsers({}));
    // The ungated options route (GET /v1/teams/options), never the settings team list:
    // GET /v1/teams is 403 without `access:settings_team`, which most inbox users lack.
    this.store.dispatch(FetchTeamOptions());
  }

  ngOnDestroy(): void {
    this.realtimeService.disconnectMailboxEvents();
    this.connectedMailboxIds.clear();
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(toggle({ toggle: false }));
  }

  // ── Private helpers (query params) ──────────────────────────────────────

  /**
   * Reads URL query params (e.g. ?folder=mentions, ?assigned_to=me) dispatched
   * by toast redirectTo links and applies the corresponding store actions so the
   * view opens with the right folder / filter pre-selected.
   */
  private applyQueryParams(): void {
    const validFolders = Object.values(InboxFolderType) as string[];

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        let applied = false;

        const folderParam = params['folder'];
        if (folderParam && validFolders.includes(folderParam)) {
          this.store.dispatch(smartInboxActions.SetActiveFolder({ folder: folderParam as InboxFolderType }));
          this.store.dispatch(smartInboxActions.ClearActiveConversation());
          applied = true;
        }

        const assignedTo = params['assigned_to'];
        if (assignedTo === 'me') {
          const currentUser = this.authService.getCurrentUser();
          if (currentUser?.id) {
            this.store.dispatch(smartInboxActions.ClearActiveConversation());
            this.store.dispatch(smartInboxActions.SetFilter({
              filter: { assigneeIds: [currentUser.id] },
            }));
            applied = true;
          }
        }

        const threadId = this.parseFirstThreadId(params['include_thread_ids']);
        if (threadId) {
          this.store.dispatch(smartInboxActions.LoadConversationFromUrl({ conversationId: threadId }));
        }

        const openDraftId = this.parseFirstThreadId(params['open_draft_id']);
        if (openDraftId) {
          // Deep-link from the workflow_send_reply_failed notification: open the
          // draft the automation created in its compose panel, floating on top of
          // the thread that the include_thread_ids branch above opens — so the
          // user reviews the draft in the conversation's context.
          this.draftService.getDraft(openDraftId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                if (res?.object) {
                  this.composeService.openFromDraft(res.object);
                }
              },
              // Draft may have been sent/deleted since — nothing to open, stay silent.
              error: () => undefined,
            });
          applied = true;
        }

        if (applied) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        }
      });
  }

  /** Parses the first numeric ID from a comma-separated query param (e.g. "123" or "123,456"). */
  private parseFirstThreadId(raw: string | undefined): number | null {
    if (!raw) return null;
    const id = parseInt(raw.split(',')[0], 10);
    return isNaN(id) ? null : id;
  }

  // ── Private helpers (real-time) ───────────────────────────────────────────

  /** Falls back to all accounts when no explicit filter is set. */
  private get effectiveActiveAccounts(): IAccount[] {
    const active = this.filter.activeAccounts ?? [];
    return active.length > 0 ? active : this.accounts;
  }

  /** Stable key for a set of accounts, order-independent. */
  private accountIdsKey(accounts: IAccount[]): string {
    return accounts.map((account) => account.id).sort((a, b) => a - b).join(',');
  }

  /**
   * Reconcile open mailbox streams against the active accounts.
   *
   * Must stay idempotent. Rebuilding them all on every `selectAccounts` emission was a
   * request loop — a fresh stream emits `connected`, the handler answers `LoadAccounts()`,
   * the new `accounts` array re-emits — and leaked server-side slots, which the backend
   * frees only when a keepalive write fails.
   */
  private syncMailboxEventStreams(): void {
    // -1 is the synthetic shared-inbox row, which has no stream.
    const desired = new Set(
      this.effectiveActiveAccounts
        .map((account) => account.id)
        .filter((id) => id !== -1)
    );

    this.connectedMailboxIds.forEach((mailboxId) => {
      if (!desired.has(mailboxId)) {
        this.realtimeService.disconnectMailboxEvent(mailboxId);
        this.connectedMailboxIds.delete(mailboxId);
      }
    });

    desired.forEach((mailboxId) => {
      if (!this.connectedMailboxIds.has(mailboxId)) {
        this.connectedMailboxIds.add(mailboxId);
        this.connectMailboxEvents(mailboxId);
      }
    });
  }

  private connectMailboxEvents(mailboxId: number): void {
    if (mailboxId === -1) return;
    this.realtimeService
      .connectMailboxEvents(mailboxId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event: RealtimeEvent) => {
          // Every branch below is scoped to a mailbox the user is looking at: there is one
          // stream per active account, but a stream can outlive a filter change by a tick.
          if (!this.effectiveActiveAccounts.some((account) => account.id === event.mailbox_id)) return;

          // Mailbox state, not list state — deliberately IN FRONT of the searchTerm gate.
          // The sync banner is about the mailbox, so suppressing it during a search would
          // leave the user on "no results" with no hint that mail is still being fetched.
          if (event.type === 'sync_status_changed') {
            this.store.dispatch(smartInboxActions.MailboxSyncStatusChanged({
              mailboxId: event.mailbox_id,
              syncStatus: event.sync_status,
              lastSyncError: event.last_sync_error,
              totalThreads: event.total_threads,
            }));
            return;
          }

          // The stream has no replay buffer and a bounded per-connection queue that drops
          // silently, so a reconnect may have missed a whole sync lifecycle. Re-read the
          // mailboxes instead of trusting a sync_status we may have stopped hearing about.
          if (event.type === 'connected') {
            this.store.dispatch(smartInboxActions.LoadAccounts());
            return;
          }

          // Everything below needs a thread. `RealtimeEvent` is a compile-time claim about a
          // payload the server owns, so check at runtime too: a fourth mailbox-scope event
          // type added on the backend would otherwise dispatch `conversationId: undefined`.
          if (event.type !== 'email_received' && event.type !== 'thread_updated') return;

          // List inserts stay suppressed during a search: the visible list is a result set,
          // and an unrelated new thread does not belong in it.
          if (this.searchTerm) return;

          this.store.dispatch(smartInboxActions.RealtimeEventReceived({
            conversationId: event.thread_id,
          }));
        },
        error: (err) => {
          // The service drops its entry when it gives up, so ours must too — else the
          // reconcile never reopens this mailbox.
          this.connectedMailboxIds.delete(mailboxId);
          console.error('[Inbox] Real-time connection error:', err);
        },
      });
  }
}
